import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import ModalService from "src/modals"
import { selectEconomyConfig, selectReactionCooldownMs } from "src/store/common"
import { selectOwnedCosmetics } from "src/store/economy"
import { selectIsGameFinished } from "src/store/game"
import {
  reactionEmoji,
  reactionImage,
  reactionText,
  reactionsIn,
  sendableReactions,
} from "src/utils/reactions"

import "./ReactionBar.css"

/* The button that sends one.

   A tray rather than a row of ten buttons permanently over the map: the map is the screen, and a
   feature nobody is obliged to use should not take a strip of it. Closed it is one key-sized
   button; open it is the player's own reactions, free ones first.

   The cooldown is the server's rule, held on the player's row (`games/reactions.py`), and this
   mirrors it only so the button can look disabled — the refusal still arrives as `ReactingTooFast`
   and is worded like every other one. Mirroring it is the difference between a button that visibly
   waits and one that appears broken; reading the *published* number rather than keeping a copy is
   what stops it coming back early into a refusal the player did nothing to earn. */

const ReactionBar = () => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [readyAt, setReadyAt] = useState(0)
  const [, forceRender] = useState(0)
  const trayRef = useRef(null)

  const economy = useSelector(selectEconomyConfig)
  const cooldownMs = useSelector(selectReactionCooldownMs)
  const owned = useSelector(selectOwnedCosmetics)
  const isFinished = useSelector(selectIsGameFinished)

  const catalogue = economy?.cosmetics
  const mine = sendableReactions(catalogue, owned)
  // Anything in the catalogue this player has not bought. Worth knowing rather than hiding: a tray
  // that showed only what you own could never tell you there is more.
  const forSale = reactionsIn(catalogue).filter((reaction) => !reaction.free && !owned.includes(reaction.id))

  // Re-render once when the cooldown ends so the buttons come back by themselves.
  useEffect(() => {
    if (!readyAt) return undefined
    const timer = setTimeout(() => forceRender((n) => n + 1), Math.max(0, readyAt - Date.now()))
    return () => clearTimeout(timer)
  }, [readyAt])

  // Click anywhere else, or press Escape, and the tray closes. Left open it would sit over the map.
  useEffect(() => {
    if (!open) return undefined
    const dismiss = (event) => {
      if (trayRef.current && !trayRef.current.contains(event.target)) setOpen(false)
    }
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", dismiss)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", dismiss)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  // Nothing to send: an older server with no reaction catalogue at all. Render nothing rather than
  // an empty tray, the same call `GoogleSignInButton` makes with no client id.
  if (isFinished || !mine.length) return null

  const waiting = Date.now() < readyAt

  const send = (reaction) => {
    if (waiting) return
    wsClient.send("game", { action: "send_reaction", reaction: reaction.id })
    setReadyAt(Date.now() + cooldownMs)
    setOpen(false)
  }

  return (
    <div className="reaction-bar" ref={trayRef}>
      {open && (
        <div className="reaction-tray" role="menu">
          {mine.map((reaction) => (
            <button
              key={reaction.id}
              type="button"
              role="menuitem"
              className="reaction-choice"
              disabled={waiting}
              title={reactionText(reaction)}
              onClick={() => send(reaction)}
            >
              {reactionImage(reaction) ? (
                <img className="reaction-choice-image" src={reactionImage(reaction)} alt="" />
              ) : (
                <span className="reaction-choice-emoji" aria-hidden="true">
                  {reactionEmoji(reaction)}
                </span>
              )}
              {/* The tray keeps the name even for a picture — a row of unlabelled faces is a
                  guessing game, and this is the one place the thing has to be *chosen*. */}
              <span className="reaction-choice-label">{reactionText(reaction)}</span>
            </button>
          ))}
          {forSale.length > 0 && (
            <button
              type="button"
              className="reaction-more"
              onClick={() => {
                setOpen(false)
                ModalService.show("ShopModal", { initialTab: "looks" })
              }}
            >
              {t("game.reactions.more", { count: forSale.length })}
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        className={`reaction-toggle${open ? " open" : ""}`}
        aria-expanded={open}
        title={t("game.reactions.open")}
        aria-label={t("game.reactions.button")}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        {/* A face, not the word (owner, 2026-10-26): "REACCIONAR" was the widest thing on the
            phone's right-hand rail and ran into the buttons under it. */}
        <span aria-hidden="true">😄</span>
      </button>
    </div>
  )
}

export default ReactionBar
